/**
 * Submittal Service
 * Handles submittal management API calls
 */

import { httpClient, ApiResponse } from './http.client';

export interface Submittal {
  submittal_id: string;
  project_id: string;
  submittal_number: string;
  title: string;
  description?: string;

  // Classification
  category: string;
  submittal_type: string;
  priority: 'low' | 'medium' | 'high' | 'urgent';

  // Assignment
  submitted_by: string;
  assigned_to: string;

  // Status & Timing
  status: 'pending-review' | 'approved' | 'approved-as-noted' | 'revise-resubmit' | 'rejected' | 'acknowledged';
  submit_date: string;
  due_date: string;
  review_date?: string;

  // References
  spec_section?: string;
  drawing_reference?: string;
  contract_reference?: string;
  revision_number?: string;

  // Additional Information
  notes?: string;
  attachments?: string[];

  // Review Information
  reviewed_by?: string;
  review_comments?: string;

  // Metadata
  created_at: string;
  updated_at: string;
}

export interface SubmittalCreate {
  organization_id: string;
  project_id: string;
  submittal_number: string;
  submitted_by: string;
  title: string;
  description?: string;
  category: string;
  submittal_type: string;
  priority?: 'low' | 'medium' | 'high' | 'urgent';
  assigned_to: string;
  submit_date: string;
  due_date: string;
  spec_section?: string;
  drawing_reference?: string;
  contract_reference?: string;
  revision_number?: string;
  notes?: string;
  attachments?: string[];
}

export interface SubmittalUpdate {
  title?: string;
  description?: string;
  category?: string;
  submittal_type?: string;
  priority?: 'low' | 'medium' | 'high' | 'urgent';
  assigned_to?: string;
  due_date?: string;
  spec_section?: string;
  drawing_reference?: string;
  contract_reference?: string;
  revision_number?: string;
  notes?: string;
  status?: 'pending-review' | 'approved' | 'approved-as-noted' | 'revise-resubmit' | 'rejected' | 'acknowledged';
  attachments?: string[];
}

export interface SubmittalReview {
  status: 'approved' | 'approved-as-noted' | 'revise-resubmit' | 'rejected' | 'acknowledged';
  review_comments: string;
  review_date: string;
}

export interface SubmittalFilters {
  project_id?: string;
  status?: string;
  priority?: string;
  category?: string;
  submittal_type?: string;
  submitted_by?: string;
  assigned_to?: string;
  due_date_from?: string;
  due_date_to?: string;
  search?: string;
}

class SubmittalService {
  /**
   * Get all submittals
   */
  async getSubmittals(page = 1, perPage = 20, filters?: SubmittalFilters): Promise<ApiResponse<{ data: Submittal[]; total: number; page: number; per_page: number }>> {
    const queryParams = new URLSearchParams({
      page: page.toString(),
      per_page: perPage.toString()
    });

    if (filters) {
      Object.entries(filters).forEach(([key, value]) => {
        if (value !== undefined && value !== null) {
          queryParams.append(key, value.toString());
        }
      });
    }

    return httpClient.get<{ data: Submittal[]; total: number; page: number; per_page: number }>(
      `/api/v1/project-management/submittals?${queryParams}`
    );
  }

  /**
   * Get submittals for a specific project
   */
  async getProjectSubmittals(projectId: string, page = 1, perPage = 50): Promise<ApiResponse<{ data: Submittal[]; total: number }>> {
    const queryParams = new URLSearchParams({
      project_id: projectId,
      page: page.toString(),
      per_page: perPage.toString()
    });

    return httpClient.get<{ data: Submittal[]; total: number }>(
      `/api/v1/project-management/submittals?${queryParams}`
    );
  }

  /**
   * Get single submittal
   */
  async getSubmittal(submittalId: string): Promise<ApiResponse<Submittal>> {
    return httpClient.get<Submittal>(`/api/v1/project-management/submittals/${submittalId}`);
  }

  /**
   * Create new submittal
   */
  async createSubmittal(submittalData: SubmittalCreate): Promise<ApiResponse<Submittal>> {
    return httpClient.post<Submittal>('/api/v1/project-management/submittals/', submittalData);
  }

  /**
   * Update submittal
   */
  async updateSubmittal(submittalId: string, submittalData: SubmittalUpdate): Promise<ApiResponse<Submittal>> {
    return httpClient.put<Submittal>(`/api/v1/project-management/submittals/${submittalId}`, submittalData);
  }

  /**
   * Delete submittal
   */
  async deleteSubmittal(submittalId: string): Promise<ApiResponse<void>> {
    return httpClient.delete<void>(`/api/v1/project-management/submittals/${submittalId}`);
  }

  /**
   * Submit review for submittal
   */
  async reviewSubmittal(submittalId: string, reviewData: SubmittalReview): Promise<ApiResponse<Submittal>> {
    const queryParams = new URLSearchParams({
      review_status: reviewData.status,
    });

    if (reviewData.review_comments) {
      queryParams.append('review_comments', reviewData.review_comments);
    }

    return httpClient.post<Submittal>(
      `/api/v1/project-management/submittals/${submittalId}/review?${queryParams}`,
      {}
    );
  }

  /**
   * Get submittals by status
   */
  async getSubmittalsByStatus(status: string, projectId?: string): Promise<ApiResponse<Submittal[]>> {
    const queryParams = new URLSearchParams({ status });
    if (projectId) {
      queryParams.append('project_id', projectId);
    }
    return httpClient.get<Submittal[]>(`/api/v1/project-management/submittals/by-status?${queryParams}`);
  }

  /**
   * Get overdue submittals
   */
  async getOverdueSubmittals(projectId?: string): Promise<ApiResponse<Submittal[]>> {
    const queryParams = projectId ? `?project_id=${projectId}` : '';
    return httpClient.get<Submittal[]>(`/api/v1/project-management/submittals/overdue${queryParams}`);
  }

  /**
   * Get submittals assigned to user
   */
  async getUserSubmittals(userId: string, projectId?: string): Promise<ApiResponse<Submittal[]>> {
    const queryParams = new URLSearchParams({ assigned_to: userId });
    if (projectId) {
      queryParams.append('project_id', projectId);
    }
    return httpClient.get<Submittal[]>(`/api/v1/project-management/submittals/assigned?${queryParams}`);
  }

  /**
   * Bulk update submittal status
   */
  async bulkUpdateSubmittalStatus(submittalIds: string[], status: string): Promise<ApiResponse<void>> {
    return httpClient.post<void>('/api/v1/project-management/submittals/bulk-status-update', {
      submittal_ids: submittalIds,
      status
    });
  }

  /**
   * Search submittals
   */
  async searchSubmittals(query: string, projectId?: string): Promise<ApiResponse<Submittal[]>> {
    const queryParams = new URLSearchParams({ q: query });
    if (projectId) {
      queryParams.append('project_id', projectId);
    }
    return httpClient.get<Submittal[]>(`/api/v1/project-management/submittals/search?${queryParams}`);
  }

  /**
   * Get submittal analytics
   */
  async getSubmittalAnalytics(projectId: string, period: 'week' | 'month' | 'quarter' = 'month'): Promise<ApiResponse<{
    total_submittals: number;
    pending_review: number;
    approved: number;
    rejected: number;
    overdue: number;
    average_review_time_days: number;
    submittals_by_category: { [key: string]: number };
    approval_rate: number;
  }>> {
    return httpClient.get<{
      total_submittals: number;
      pending_review: number;
      approved: number;
      rejected: number;
      overdue: number;
      average_review_time_days: number;
      submittals_by_category: { [key: string]: number };
      approval_rate: number;
    }>(`/api/v1/project-management/submittals/analytics?project_id=${projectId}&period=${period}`);
  }

  /**
   * Get next submittal number for a project
   */
  async getNextSubmittalNumber(projectId: string): Promise<ApiResponse<{ submittal_number: string }>> {
    return httpClient.get<{ submittal_number: string }>(`/api/v1/project-management/submittals/next-submittal-number?project_id=${projectId}`);
  }
}

// Export singleton instance
export const submittalService = new SubmittalService();
